import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

const V = (a) => new THREE.Vector3(...a);

const RIM = new THREE.Color("#7a0010");
const addRim = (shader, rim) => {
  shader.uniforms.uRim = rim;
  shader.uniforms.uRimColor = { value: RIM };
  shader.fragmentShader = "uniform float uRim;\nuniform vec3 uRimColor;\n" + shader.fragmentShader.replace("#include <emissivemap_fragment>",
    "#include <emissivemap_fragment>\nfloat rimF = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 1.0);")
    .replace("#include <opaque_fragment>", "#include <opaque_fragment>\ngl_FragColor.rgb = mix(gl_FragColor.rgb, uRimColor, clamp(rimF * uRim, 0.0, 0.97));");
};

export const OrganMat = ({ color, active, dim }) => {
  const ref = useRef();
  const rim = useMemo(() => ({ value: 0 }), []);
  useFrame(({ clock }) => {
    const p = 0.5 + 0.5 * Math.sin(clock.elapsedTime * 4);
    rim.value = active ? 1.6 + 1.2 * p : 0;
    if (ref.current) ref.current.emissiveIntensity = active ? 0.5 + 0.3 * p : 0.22;
  });
  return (
    <meshPhysicalMaterial key={dim ? "d" : "n"} ref={ref} color={color} roughness={0.38} clearcoat={0.7} clearcoatRoughness={0.25}
      emissive={color} emissiveIntensity={0.28} transparent={dim} opacity={dim ? 0.25 : 1} depthWrite={!dim}
      onBeforeCompile={(s) => addRim(s, rim)} />
  );
};

export const Tube = ({ pts, r, seg = 64, children }) => {
  const curve = useMemo(() => new THREE.CatmullRomCurve3(pts.map(V)), [pts]);
  return <mesh><tubeGeometry args={[curve, seg, r, 14, false]} />{children}</mesh>;
};

export const Blob = ({ pos, r, rot = [0, 0, 0], bumps = 0, children }) => {
  const geo = useMemo(() => {
    const g = new THREE.SphereGeometry(1, 48, 36);
    if (bumps) {
      const p = g.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const v = new THREE.Vector3().fromBufferAttribute(p, i);
        const n = Math.sin(v.x * bumps) * Math.sin(v.y * bumps * 1.3) * Math.sin(v.z * bumps * 0.9);
        v.multiplyScalar(1 + 0.045 * n);
        p.setXYZ(i, v.x, v.y, v.z);
      }
      g.computeVertexNormals();
    }
    return g;
  }, [bumps]);
  return <mesh geometry={geo} position={pos} scale={r} rotation={rot}>{children}</mesh>;
};

export const Lathe = ({ pos, profile, scale = [1, 1, 1], rot = [0, 0, 0], children }) => {
  const geo = useMemo(() => new THREE.LatheGeometry(profile.map(([x, y]) => new THREE.Vector2(x, y)), 40), [profile]);
  return <mesh geometry={geo} position={pos} scale={scale} rotation={rot}>{children}</mesh>;
};

export const Bean = ({ pos, rot, children }) => (
  <mesh position={pos} rotation={rot} scale={[1, 1, 0.75]}>
    <torusGeometry args={[0.02, 0.019, 20, 32, Math.PI * 1.15]} />
    {children}
  </mesh>
);
